// People: Blue Market staff and client users, pending invitations, roles and access.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { useLiveTopics } from '../live';
import { CopyField, Icon, Menu, Modal, ago, useConfirm, useToast } from '../ui';
import type { Client, Invitation, Role, UserRow } from '../types';
import { ROLE_LABELS } from '../types';
import { Page, useCrumbs } from './Shell';

const STAFF: Role[] = ['owner', 'admin', 'staff', 'sales'];
const CLIENT: Role[] = ['client_admin', 'client_member', 'client_viewer'];
const INVITABLE: Partial<Record<Role, Role[]>> = {
  owner: [...STAFF, ...CLIENT], admin: ['admin', 'staff', 'sales', ...CLIENT], staff: CLIENT, client_admin: CLIENT,
};
const ROLE_HELP: Record<Role, string> = {
  owner: 'Everything, including workspace settings.', admin: 'Everything except workspace settings.',
  staff: 'Clients, CRM, projects, designs and tickets.', sales: 'Clients and CRM only.',
  client_admin: 'Their projects, tickets and comments, and can invite colleagues.',
  client_member: 'Their projects: create tickets and comment on designs ready for comment.', client_viewer: 'View their projects only.',
};

export function People() {
  const { me, can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const setCrumbs = useCrumbs();
  const { confirm, dialog } = useConfirm();
  const [inviting, setInviting] = useState(false);
  const [filter, setFilter] = useState<'all' | 'staff' | 'clients'>('all');
  useLiveTopics(me?.is_staff ? 'workspace' : me?.client ? `client:${me.client.id}` : null);
  useEffect(() => setCrumbs([{ label: 'People' }]), [setCrumbs]);
  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: () => get<UserRow[]>('/api/users') });
  const { data: invitations = [] } = useQuery({ queryKey: ['invitations'], queryFn: () => get<Invitation[]>('/api/invitations') });
  const shown = users.filter((u) => filter === 'all' || (filter === 'staff' ? u.is_staff : !u.is_staff));
  const invitable = INVITABLE[me?.role ?? 'client_viewer'] ?? [];

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UserRow> }) => patch<UserRow>(`/api/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (e: Error) => toast(e.message, true),
  });
  const toggle = async (u: UserRow) => {
    const disabling = u.status === 'active';
    if (disabling && !(await confirm(`Disable ${u.name}?`, { body: 'They are signed out everywhere straight away and can no longer sign in. You can re-enable them later.', ok: 'Disable', danger: true }))) return;
    update.mutate({ id: u.id, body: { status: disabling ? 'disabled' : 'active' } });
  };
  const [resetLink, setResetLink] = useState<{ name: string; url: string } | null>(null);
  const makeResetLink = async (u: UserRow) => {
    if (!(await confirm(`Make a password reset link for ${u.name}?`, { body: 'Anyone with the link can set a new password for this account for the next 24 hours, so send it to them directly. Their current password keeps working until the link is used.', ok: 'Make link' }))) return;
    try { setResetLink({ name: u.name, url: (await post<{ url: string }>(`/api/users/${u.id}/password-reset`)).url }); } catch (e) { toast((e as Error).message, true); }
  };
  const reset2fa = async (u: UserRow) => {
    if (!(await confirm(`Reset two-factor sign-in for ${u.name}?`, { body: 'Use this when they have lost their phone and recovery codes. Their authenticator and recovery codes stop working and they are signed out everywhere.' + (u.is_staff ? ' They set up a new authenticator at their next sign-in.' : ''), ok: 'Reset two-factor', danger: true }))) return;
    try { await post(`/api/users/${u.id}/reset-2fa`); qc.invalidateQueries({ queryKey: ['users'] }); toast(`Two-factor sign-in reset for ${u.name}`); } catch (e) { toast((e as Error).message, true); }
  };
  const revoke = async (i: Invitation) => {
    if (!(await confirm(`Withdraw the invitation for ${i.email}?`, { ok: 'Withdraw', danger: true }))) return;
    try { await del(`/api/invitations/${i.id}`); qc.invalidateQueries({ queryKey: ['invitations'] }); } catch (e) { toast((e as Error).message, true); }
  };
  const canEdit = (u: UserRow) => u.id !== me?.id && (u.role !== 'owner' || me?.role === 'owner') && (u.is_staff ? can('users.manage') : can('users.manage') || (me?.role === 'client_admin' && u.client?.id === me.client?.id));

  return (
    <Page title="People" eyebrow={me?.is_staff ? 'Admin' : me?.client?.name}
          actions={invitable.length > 0 && <button className="btn primary" onClick={() => setInviting(true)}><Icon name="plus" />Invite someone</button>}>
      {me?.is_staff && (
        <div className="row" style={{ marginBottom: 14 }}>
          {(['all', 'staff', 'clients'] as const).map((f) => <button key={f} className={`btn sm ${filter === f ? 'primary' : ''}`} onClick={() => setFilter(f)}>{f === 'all' ? 'Everyone' : f === 'staff' ? 'Blue Market' : 'Client users'}</button>)}
        </div>
      )}
      <div className="card table-wrap">
        <table className="table">
          <thead><tr><th>Name</th><th>Email</th><th>Organisation</th><th>Role</th><th>Two-factor</th><th>Last sign-in</th><th /></tr></thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} style={u.status === 'disabled' ? { opacity: .55 } : undefined}>
                <td><b>{u.name}</b>{u.id === me?.id && <span className="muted small"> (you)</span>}</td>
                <td className="muted">{u.email}</td>
                <td>{u.client ? u.client.name : <span className="badge brand">Blue Market</span>}</td>
                <td>
                  {canEdit(u) ? (
                    <select className="input" style={{ minHeight: 30, padding: '3px 8px', width: 'auto' }} value={u.role}
                            onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value as Role } })} aria-label={`Role for ${u.name}`}>
                      {(u.is_staff ? STAFF : CLIENT).filter((r) => r === u.role || invitable.includes(r)).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  ) : ROLE_LABELS[u.role]}
                </td>
                <td>{u.totp_enabled ? <span className="badge ok">On</span> : <span className={`badge ${u.is_staff ? 'warn' : ''}`}>Off</span>}</td>
                <td className="muted">{u.status === 'disabled' ? 'Disabled' : ago(u.last_login_at)}</td>
                <td style={{ textAlign: 'right' }}>{canEdit(u) && <Menu label={`Actions for ${u.name}`} items={[
                  ...(u.status === 'active' ? [{ label: 'Password reset link', onClick: () => makeResetLink(u) }] : []),
                  ...(u.status === 'active' && u.totp_enabled ? [{ label: 'Reset two-factor', onClick: () => reset2fa(u) }] : []),
                  { label: u.status === 'active' ? 'Disable' : 'Enable', onClick: () => toggle(u), danger: u.status === 'active' },
                ]} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {invitations.length > 0 && (
        <section className="card" style={{ marginTop: 18 }}>
          <div className="card-head"><h2>Pending invitations</h2></div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Email</th><th>Role</th><th>Organisation</th><th>Invited by</th><th>Expires</th><th /></tr></thead>
              <tbody>{invitations.map((i) => (
                <tr key={i.id}>
                  <td>{i.email}</td><td>{ROLE_LABELS[i.role]}</td><td>{i.client?.name ?? 'Blue Market'}</td>
                  <td className="muted">{i.invited_by ?? '–'}</td><td className="muted">{new Date(i.expires_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</td>
                  <td style={{ textAlign: 'right' }}><button className="btn sm ghost danger" onClick={() => revoke(i)}>Withdraw</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      )}
      {inviting && <InviteDialog onClose={() => setInviting(false)} clientId={me?.is_staff ? undefined : me?.client?.id} clientName={me?.client?.name} />}
      {resetLink && (
        <Modal title={`Password reset link for ${resetLink.name}`} onClose={() => setResetLink(null)} width={520}>
          <p className="muted small" style={{ margin: 0 }}>Send this to them directly. It works once, for 24 hours. It's shown only now.</p>
          <CopyField value={resetLink.url} />
          <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn primary" onClick={() => setResetLink(null)}>Done</button></div>
        </Modal>
      )}
      {dialog}
    </Page>
  );
}

export function InviteDialog({ onClose, clientId, clientName }: { onClose: () => void; clientId?: string; clientName?: string }) {
  const { me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const invitable = INVITABLE[me?.role ?? 'client_viewer'] ?? [];
  const [kind, setKind] = useState<'staff' | 'client'>(clientId || !me?.is_staff ? 'client' : 'staff');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [client, setClient] = useState(clientId ?? '');
  const roles = useMemo(() => invitable.filter((r) => (kind === 'staff' ? STAFF : CLIENT).includes(r)), [invitable, kind]);
  const [role, setRole] = useState<Role>(roles[roles.length > 2 ? 2 : 0] ?? 'client_member');
  const [link, setLink] = useState('');
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => get<Client[]>('/api/clients'), enabled: Boolean(me?.is_staff && !clientId && kind === 'client') });
  useEffect(() => { if (!roles.includes(role)) setRole(roles[0]); }, [roles, role]);
  const chosen = clients.find((c) => c.id === client);

  const send = useMutation({
    mutationFn: () => post<{ url: string }>('/api/invitations', { email, name: name || null, role, client_id: kind === 'client' ? client || clientId : null }),
    onSuccess: (r) => { setLink(r.url); qc.invalidateQueries({ queryKey: ['invitations'] }); },
    onError: (e: Error) => toast(e.message, true),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); send.mutate(); };

  if (link) return (
    <Modal title={`Invitation for ${email}`} onClose={onClose}>
      <p className="muted small" style={{ margin: 0 }}>Send this link to them. <b>It's shown only now</b> and works once, for 7 days. Email delivery will be added later.</p>
      <CopyField value={link} />
      <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn primary" onClick={onClose}>Done</button></div>
    </Modal>
  );
  return (
    <Modal title={clientName ? `Invite someone from ${clientName}` : 'Invite someone'} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        {me?.is_staff && !clientId && invitable.some((r) => STAFF.includes(r)) && (
          <div className="row">
            <button type="button" className={`btn sm ${kind === 'staff' ? 'primary' : ''}`} onClick={() => setKind('staff')}>Blue Market</button>
            <button type="button" className={`btn sm ${kind === 'client' ? 'primary' : ''}`} onClick={() => setKind('client')}>A client's person</button>
          </div>
        )}
        {kind === 'client' && me?.is_staff && !clientId && (
          <label className="field"><span>Client</span>
            <select className="input" value={client} onChange={(e) => setClient(e.target.value)} required>
              <option value="">Choose a client</option>
              {clients.map((c) => <option key={c.id} value={c.id} disabled={!c.domains.length}>{c.name}{c.domains.length ? '' : ' (no allowed domains yet)'}</option>)}
            </select>
            {chosen && <span className="hint">Allowed: {chosen.domains.map((d) => '@' + d).join(', ')}</span>}
          </label>
        )}
        <label className="field"><span>Email</span><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></label>
        <label className="field"><span>Name <span className="hint">(optional)</span></span><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field"><span>Role</span>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>{roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select>
          <span className="hint">{ROLE_HELP[role]}</span>
        </label>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={send.isPending}>{send.isPending && <span className="spin" />}Create invitation</button>
        </div>
      </form>
    </Modal>
  );
}
